/**
 * Utility functions for handling payments
 */

/**
 * Parse PayNow return URL parameters
 * When PayNow redirects back to the application, it includes parameters in the URL
 * This function extracts those parameters to verify the payment
 * 
 * @returns Object containing payment parameters or null if not a PayNow return
 */
export const parsePaynowReturnParams = (sourceUrl?: string): {
  reference?: string;
  status?: string;
  pollUrl?: string;
  hash?: string;
} | null => {
  // Only run in browser environment when no explicit URL is provided
  if (typeof window === 'undefined' && !sourceUrl) return null;

  const parsedUrl = new URL(sourceUrl ?? window.location.href);
  const urlParams = parsedUrl.searchParams;
  
  // Check if this is a return from PayNow
  // PayNow typically includes at least a reference parameter
  const reference = urlParams.get('reference');
  
  if (!reference) return null;
  
  return {
    reference: reference,
    status: urlParams.get('status') || undefined,
    pollUrl: urlParams.get('pollurl') || undefined,
    hash: urlParams.get('hash') || undefined,
  };
};

/**
 * Check if the current URL contains PayNow return parameters
 * 
 * @returns boolean indicating if the current URL contains PayNow return parameters
 */
export const isPaynowReturn = (sourceUrl?: string): boolean => {
  return parsePaynowReturnParams(sourceUrl) !== null;
};

/**
 * Clear PayNow return parameters from the URL
 * This prevents the parameters from being processed multiple times
 */
export const clearPaynowReturnParams = (): void => {
  // Only run in browser environment
  if (typeof window === 'undefined') return;
  
  const url = new URL(window.location.href);
  
  // Remove PayNow-specific parameters
  url.searchParams.delete('reference');
  url.searchParams.delete('status');
  url.searchParams.delete('pollurl');
  url.searchParams.delete('hash');
  
  // Update the URL without reloading the page
  window.history.replaceState({}, document.title, url.toString());
};